import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  onSnapshot,
  serverTimestamp,
  arrayUnion,
} from 'firebase/firestore';
import type { Unsubscribe } from 'firebase/firestore';
import { db } from '../firebase/config';
import type {
  ThinkSpace,
  CanvasElement,
  SharedUser,
  CreateThinkSpaceInput,
  InviteUserInput,
  SharePermission,
  ImageLibraryItem,
} from '@/types/thinkspace';
import type { User } from 'firebase/auth';

const COLLECTION = 'thinkspaces';

export const thinkspacesService = {
  // Get a single ThinkSpace by ID
  getById: async (id: string): Promise<ThinkSpace | null> => {
    const docRef = doc(db, COLLECTION, id);
    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) return null;
    return { id: docSnap.id, ...docSnap.data() } as ThinkSpace;
  },

  // Get all ThinkSpaces owned by user
  getOwned: async (userId: string): Promise<ThinkSpace[]> => {
    const q = query(
      collection(db, COLLECTION),
      where('ownerId', '==', userId),
      orderBy('updatedAt', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }) as ThinkSpace);
  },

  // Get all ThinkSpaces shared with user
  getSharedWithMe: async (userEmail: string): Promise<ThinkSpace[]> => {
    // Firestore doesn't support array-contains on objects well, so we'll filter client-side
    const allSpaces = await getDocs(collection(db, COLLECTION));
    return allSpaces.docs
      .map((d) => ({ id: d.id, ...d.data() }) as ThinkSpace)
      .filter((space) =>
        space.sharedWith?.some(
          (share) => share.email === userEmail && share.status === 'accepted'
        )
      );
  },

  // Get pending invitations for user
  getPendingInvitations: async (userEmail: string): Promise<ThinkSpace[]> => {
    const allSpaces = await getDocs(collection(db, COLLECTION));
    return allSpaces.docs
      .map((d) => ({ id: d.id, ...d.data() }) as ThinkSpace)
      .filter((space) =>
        space.sharedWith?.some(
          (share) => share.email === userEmail && share.status === 'pending'
        )
      );
  },

  // Subscribe to a ThinkSpace for real-time updates
  subscribe: (
    id: string,
    callback: (space: ThinkSpace | null) => void,
    onError?: (error: Error) => void
  ): Unsubscribe => {
    const docRef = doc(db, COLLECTION, id);
    return onSnapshot(
      docRef,
      (snapshot) => {
        if (!snapshot.exists()) {
          callback(null);
          return;
        }
        callback({ id: snapshot.id, ...snapshot.data() } as ThinkSpace);
      },
      onError
    );
  },

  // Create a new ThinkSpace
  create: async (input: CreateThinkSpaceInput, user: User): Promise<string> => {
    const docRef = doc(collection(db, COLLECTION));
    const thinkSpace: Omit<ThinkSpace, 'id'> = {
      name: input.name,
      description: input.description || '',
      ownerId: user.uid,
      ownerEmail: user.email || '',
      ownerName: user.displayName || '',
      elements: [],
      sharedWith: [],
      imageLibrary: [],
      createdAt: serverTimestamp() as any,
      updatedAt: serverTimestamp() as any,
      canvasSize: { width: 3000, height: 2000 },
      backgroundColor: '#f5f5f5',
    };
    await setDoc(docRef, thinkSpace);
    return docRef.id;
  },

  // Update ThinkSpace metadata
  update: async (
    id: string,
    data: Partial<Pick<ThinkSpace, 'name' | 'description' | 'canvasSize' | 'backgroundColor'>>
  ): Promise<void> => {
    const docRef = doc(db, COLLECTION, id);
    await updateDoc(docRef, {
      ...data,
      updatedAt: serverTimestamp(),
    });
  },

  // Delete a ThinkSpace
  delete: async (id: string): Promise<void> => {
    const docRef = doc(db, COLLECTION, id);
    await deleteDoc(docRef);
  },

  // Update all elements (for batch operations)
  updateElements: async (id: string, elements: CanvasElement[]): Promise<void> => {
    const docRef = doc(db, COLLECTION, id);
    await updateDoc(docRef, {
      elements,
      updatedAt: serverTimestamp(),
    });
  },

  // Add a single element
  addElement: async (id: string, element: CanvasElement): Promise<void> => {
    const docRef = doc(db, COLLECTION, id);
    await updateDoc(docRef, {
      elements: arrayUnion(element),
      updatedAt: serverTimestamp(),
    });
  },

  // Invite a user to share
  inviteUser: async (
    id: string,
    input: InviteUserInput,
    invitedBy: string
  ): Promise<void> => {
    const docRef = doc(db, COLLECTION, id);
    const sharedUser: SharedUser = {
      id: crypto.randomUUID(),
      email: input.email.toLowerCase(),
      displayName: null,
      photoURL: null,
      permission: input.permission,
      status: 'pending',
      invitedAt: serverTimestamp() as any,
      respondedAt: null,
      invitedBy,
    };
    await updateDoc(docRef, {
      sharedWith: arrayUnion(sharedUser),
      updatedAt: serverTimestamp(),
    });
  },

  // Accept or decline invitation
  respondToInvitation: async (
    id: string,
    userEmail: string,
    accept: boolean,
    user: User
  ): Promise<void> => {
    const space = await thinkspacesService.getById(id);
    if (!space) throw new Error('ThinkSpace not found');

    const updatedSharedWith = space.sharedWith.map((share) => {
      if (share.email.toLowerCase() === userEmail.toLowerCase()) {
        return {
          ...share,
          status: accept ? 'accepted' : 'declined',
          respondedAt: serverTimestamp(),
          displayName: user.displayName,
          photoURL: user.photoURL,
        };
      }
      return share;
    });

    const docRef = doc(db, COLLECTION, id);
    await updateDoc(docRef, {
      sharedWith: updatedSharedWith,
      updatedAt: serverTimestamp(),
    });
  },

  // Update user permission
  updatePermission: async (
    id: string,
    userEmail: string,
    permission: SharePermission
  ): Promise<void> => {
    const space = await thinkspacesService.getById(id);
    if (!space) throw new Error('ThinkSpace not found');

    const updatedSharedWith = space.sharedWith.map((share) => {
      if (share.email.toLowerCase() === userEmail.toLowerCase()) {
        return { ...share, permission };
      }
      return share;
    });

    const docRef = doc(db, COLLECTION, id);
    await updateDoc(docRef, {
      sharedWith: updatedSharedWith,
      updatedAt: serverTimestamp(),
    });
  },

  // Remove shared user
  removeSharedUser: async (id: string, userEmail: string): Promise<void> => {
    const space = await thinkspacesService.getById(id);
    if (!space) throw new Error('ThinkSpace not found');

    const updatedSharedWith = space.sharedWith.filter(
      (share) => share.email.toLowerCase() !== userEmail.toLowerCase()
    );

    const docRef = doc(db, COLLECTION, id);
    await updateDoc(docRef, {
      sharedWith: updatedSharedWith,
      updatedAt: serverTimestamp(),
    });
  },

  // Add image to library
  addImageToLibrary: async (id: string, image: ImageLibraryItem): Promise<void> => {
    const docRef = doc(db, COLLECTION, id);
    await updateDoc(docRef, {
      imageLibrary: arrayUnion(image),
      updatedAt: serverTimestamp(),
    });
  },

  // Remove image from library
  removeImageFromLibrary: async (id: string, imageId: string): Promise<void> => {
    const space = await thinkspacesService.getById(id);
    if (!space) throw new Error('ThinkSpace not found');

    const updatedLibrary = (space.imageLibrary || []).filter((img) => img.id !== imageId);

    const docRef = doc(db, COLLECTION, id);
    await updateDoc(docRef, {
      imageLibrary: updatedLibrary,
      updatedAt: serverTimestamp(),
    });
  },

  // Update image library
  updateImageLibrary: async (id: string, imageLibrary: ImageLibraryItem[]): Promise<void> => {
    const docRef = doc(db, COLLECTION, id);
    await updateDoc(docRef, {
      imageLibrary,
      updatedAt: serverTimestamp(),
    });
  },

  // Check if user has access
  checkAccess: (
    space: ThinkSpace,
    userId: string,
    userEmail: string
  ): { hasAccess: boolean; canEdit: boolean; isOwner: boolean } => {
    if (space.ownerId === userId) {
      return { hasAccess: true, canEdit: true, isOwner: true };
    }

    const share = space.sharedWith.find(
      (s) => s.email.toLowerCase() === userEmail.toLowerCase() && s.status === 'accepted'
    );

    if (share) {
      return {
        hasAccess: true,
        canEdit: share.permission === 'write',
        isOwner: false,
      };
    }

    return { hasAccess: false, canEdit: false, isOwner: false };
  },
};
